import type { FC } from 'react';
import { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import { useTickets } from 'contexts/TicketsContext';
import { RoutePathsE } from 'declarations/enums/routes';
import { ActiveTabTypeE } from 'declarations/enums/tab';

import { ContentWrapStyled, LayoutStyled } from './TicketsPageLayout.styled';

const TicketsPageLayout: FC = (): JSX.Element => {
  const { openedTicketTabs, openedScheduleTicketTabs, activeTab } = useTickets();
  const location = useLocation();

  const isViewingTicketDetail = location.pathname.startsWith(`${RoutePathsE.TICKET_DETAIL}/`);
  const currentTicketId = isViewingTicketDetail
    ? location.pathname.replace(`${RoutePathsE.TICKET_DETAIL}/`, '')
    : null;

  const effectiveSection = useMemo((): ActiveTabTypeE => {
    if (currentTicketId) {
      const isScheduleTicket = Object.values(openedScheduleTicketTabs).includes(currentTicketId);
      const isNormalTicket = Object.values(openedTicketTabs).includes(currentTicketId);
      if (isScheduleTicket) return ActiveTabTypeE.Schedule;
      if (isNormalTicket) return ActiveTabTypeE.Tickets;
    }
    return activeTab;
  }, [currentTicketId, openedScheduleTicketTabs, openedTicketTabs, activeTab]);

  const currentOpenedTabs =
    effectiveSection === ActiveTabTypeE.Schedule ? openedScheduleTicketTabs : openedTicketTabs;
  const hasTicketTabs =
    (effectiveSection === ActiveTabTypeE.Tickets || effectiveSection === ActiveTabTypeE.Schedule) &&
    Object.keys(currentOpenedTabs).length > 0;

  const headerOffsetPx = 64 + (hasTicketTabs ? 40 : 0);

  return (
    <LayoutStyled headerOffsetPx={headerOffsetPx}>
      <ContentWrapStyled fullHeight={isViewingTicketDetail}>
        <Outlet />
      </ContentWrapStyled>
    </LayoutStyled>
  );
};

export default TicketsPageLayout;
